import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  Typography,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
} from "@mui/material";
import _ from "lodash";
import { useState } from "react";

import ConversionErrorTable from "@/components/membership/Preview/ConversionErrorTable";
import LookupErrorTable from "@/components/membership/Preview/LookupErrorTable";
import {
  calculatePercentageChange,
  breaksChangeThreshold,
  styledBrokenThreshold,
  formatPercentageChange,
  getEmployerNameChangeData,
  formatCurrency,
} from "@/components/membership/Preview/previewUtils";
import SiteIcons from "@/components/SiteIcons/SiteIcons";

export const Errors = ({ errors }) => {
  return (
    <Box data-testid="confirmation-errors" sx={{ marginBottom: "10px" }}>
      <Accordion defaultExpanded disableGutters sx={{ boxShadow: "none" }}>
        <SummaryAccordionHeader
          expanded={true}
          styles={{ color: "white", backgroundColor: "var(--seiu-red)" }}
        >
          ERRORS
        </SummaryAccordionHeader>
        <AccordionDetails className="preview-accordian-details">
          <Typography sx={{ fontStyle: "italic", marginTop: "1rem" }}>
            The following data issues must be corrected in order to confirm this
            file:
          </Typography>
          <List>
            {errors.map((item, index) => (
              <ListItem key={`please-resolve-${index}`}>{item}</ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export const Warnings = ({ warnings }) => {
  return (
    <Box sx={{ marginBottom: "10px" }}>
      <Accordion
        disableGutters
        data-testid="conversion-errors"
        sx={{ boxShadow: "none" }}
      >
        <SummaryAccordionHeader styles={{ color: "#ffa500" }}>
          TEMPLATE WARNINGS
        </SummaryAccordionHeader>
        <AccordionDetails className="preview-accordian-details">
          <ConversionErrorTable errors={warnings} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export const LookupTableWarnings = ({ problems }) => {
  if (!_.isEmpty(problems)) {
    return (
      <Box>
        {problems ? (
          <Accordion
            disableGutters
            data-testid="lookup-table-warnings"
            sx={{ boxShadow: "none" }}
          >
            <SummaryAccordionHeader styles={{ color: "#ffa500" }}>
              LOOKUP TABLE WARNINGS
            </SummaryAccordionHeader>
            <AccordionDetails className="preview-accordian-details">
              <Typography sx={{ fontStyle: "italic" }}>
                These columns have values which have not been mapped in lookup
                table.
              </Typography>
              {Object.entries(problems).map(
                ([colnames, unmappedVals], index) => (
                  <LookupErrorTable
                    key={index}
                    colnames={colnames}
                    unmappedVals={unmappedVals}
                  />
                )
              )}
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Box>
    );
  }
};

const ChangeInPercentage = ({ prior, current }) => {
  if (prior && current) {
    const changeInPercentage = calculatePercentageChange(prior, current);
    let displayPercentageChange;
    if (breaksChangeThreshold(changeInPercentage)) {
      displayPercentageChange = styledBrokenThreshold(changeInPercentage);
    } else {
      displayPercentageChange = formatPercentageChange(changeInPercentage);
    }
    return <>{displayPercentageChange}</>;
  }
  return (
    <>
      <i>{"Unable to calculate"}</i>
    </>
  );
};

export const ComparisonTable = ({ summary }) => {
  const employerNameData = getEmployerNameChangeData(summary);
  const rows = [
    {
      name: "Uploaded rows",
      current: summary.record_count_changes?.current,
      prior: summary.record_count_changes?.prior,
      percentageChange: (
        <ChangeInPercentage
          prior={summary.record_count_changes?.prior}
          current={summary.record_count_changes?.current}
        />
      ),
    },
    {
      name: "COPE monthly amount",
      current: formatCurrency(summary.cope_monthly?.current),
      prior: formatCurrency(summary.cope_monthly?.prior),
      percentageChange: (
        <ChangeInPercentage
          prior={summary.cope_monthly?.prior}
          current={summary.cope_monthly?.current}
        />
      ),
    },
    {
      name: "COPE average per COPE contributor",
      current: formatCurrency(summary.cope_monthly?.current_mean),
      prior: formatCurrency(summary.cope_monthly?.prior_mean),
      percentageChange: (
        <ChangeInPercentage
          prior={summary.cope_monthly?.prior_mean}
          current={summary.cope_monthly?.current_mean}
        />
      ),
    },
    {
      name: "COPE contributors",
      current: summary.cope_monthly?.current_contributors || "--",
      prior: summary.cope_monthly?.prior_contributors || "--",
      percentageChange: (
        <ChangeInPercentage
          prior={summary.cope_monthly?.prior_contributors}
          current={summary.cope_monthly?.current_contributors}
        />
      ),
    },
    {
      name: "Employer names",
      current: employerNameData.current || "--",
      prior: employerNameData.prior || "--",
      percentageChange: (
        <ChangeInPercentage
          prior={employerNameData.prior}
          current={employerNameData.current}
        />
      ),
    },
  ];

  return (
    <TableContainer>
      <Table
        sx={{ minWidth: 650, backgroundColor: "white" }}
        aria-label="simple table"
      >
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell align="right">Last Upload</TableCell>
            <TableCell align="right">Current Upload</TableCell>
            <TableCell align="right">Percentage Change</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.name}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {row.name}
              </TableCell>
              <TableCell align="right">{row.prior}</TableCell>
              <TableCell align="right">{row.current}</TableCell>
              <TableCell align="right">{row.percentageChange}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const FieldsChanged = ({ title, fields }) => {
  if (!fields) {
    return null;
  }
  return (
    <Box sx={{ marginBottom: "10px" }}>
      <Accordion
        disabled={fields.length === 0}
        disableGutters
        sx={{ boxShadow: "none" }}
      >
        <SummaryAccordionHeader>
          {title}: {fields.length}
        </SummaryAccordionHeader>
        <AccordionDetails className="preview-accordian-details">
          <List>
            {fields.map((item, index) => (
              <ListItem key={`num-fields-added-${index}`}>{item}</ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export const ProcessingDuration = ({ duration }) => {
  if (duration) {
    const durationInSeconds = new Date(duration * 1000)
      .toISOString()
      .slice(11, 19);
    return <Typography>Processing Duration: {durationInSeconds}</Typography>;
  }
  return null;
};

const SummaryAccordionHeader = (props) => {
  const [expanded, setExpanded] = useState(props.expanded ? true : false);
  return (
    <AccordionSummary
      aria-controls="panel2-content"
      id="panel2-header"
      onClick={() => setExpanded(!expanded)}
      sx={props.styles}
    >
      <SiteIcons iconstring={expanded ? "remove" : "add"} />
      <Typography sx={{ marginLeft: "8px" }}>{props.children}</Typography>
    </AccordionSummary>
  );
};
